import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, FormControlLabel, Paper, Stack,
  Switch, TextField, Typography,
} from '@mui/material';
import { jevService, JevSettingsResponse } from '../services/jev-service';

/**
 * Settings → AI analysis → Jev (TypeSafe) auto-categorisation (issue #26).
 *
 * The household brings its own TypeSafe key. FolioWorth then categorises
 * what is still uncategorised after every Plaid sync and import: rules first,
 * then Jev. Answers at or above the threshold are applied; the rest wait in
 * the Transactions page's "Needs review" queue.
 *
 * The key field is write-only. A saved key is never shown again, only its
 * last four characters, and leaving the field blank keeps the stored key.
 */
export const JevSettingsCard: React.FC = () => {
  const [data, setData] = useState<JevSettingsResponse | null>(null);
  const [key, setKey] = useState('');
  const [threshold, setThreshold] = useState('0.85');
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const apply = (r: JevSettingsResponse) => {
    setData(r);
    setEnabled(r.settings.enabled);
    setThreshold(String(r.settings.threshold));
  };

  useEffect(() => {
    jevService.getSettings().then(apply).catch((e: any) => setError(e?.message ?? 'Could not load Jev settings'));
  }, []);

  const save = async () => {
    const t = Number(threshold);
    if (!(t > 0 && t <= 1)) {
      setError('Threshold must be greater than 0 and at most 1');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await jevService.saveSettings({
        ...(key.trim() ? { api_key: key.trim() } : {}),
        enabled,
        threshold: t,
      });
      // Drop the secret from memory as soon as the server has it.
      setKey('');
      apply(r);
      setNotice('Jev settings saved');
    } catch (e: any) {
      setError(e?.message ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      apply(await jevService.removeKey());
      setNotice('Jev key removed');
    } catch (e: any) {
      setError(e?.message ?? 'Remove failed');
    } finally {
      setBusy(false);
    }
  };

  if (!data && !error) {
    return <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>;
  }
  const s = data?.settings;

  return (
    <Paper sx={{ p: 2.5 }} aria-label="Jev auto-categorisation">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" fontWeight={600}>Jev auto-categorisation</Typography>
        {s?.has_key && (
          <Chip size="small" variant="outlined" sx={{ height: 20 }} label={`key ••••${s.key_last4 ?? ''}`} />
        )}
        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              inputProps={{ 'aria-label': 'Jev enabled' }}
            />
          }
          label={<Typography variant="caption">Enabled</Typography>}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        With your own TypeSafe key, uncategorised transactions are categorised after every sync and
        import — your rules first, then Jev. Only the date, description, amount, account name and
        bank label are sent. Answers below the threshold wait for you under Transactions → Needs review.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField
          size="small"
          label={s?.has_key ? 'Replace key (optional)' : 'TypeSafe API key'}
          type="password"
          autoComplete="new-password"
          inputProps={{ autoComplete: 'new-password', 'data-1p-ignore': true, 'data-lpignore': true }}
          value={key}
          onChange={(e) => setKey(e.target.value)}
          helperText={s?.has_key ? 'Leave blank to keep the stored key' : 'Stored encrypted; never shown again'}
          sx={{ flex: 1, minWidth: 240 }}
        />
        <TextField
          size="small"
          label="Confidence threshold"
          type="number"
          inputProps={{ min: 0.5, max: 1, step: 0.01 }}
          value={threshold}
          onChange={(e) => setThreshold(e.target.value)}
          helperText="Applied at or above; reviewed below"
          sx={{ width: 200 }}
        />
      </Stack>

      {s && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {s.has_key && s.key_set_at ? `Key set ${new Date(s.key_set_at).toLocaleString()}. ` : ''}
          {`${data?.used_today ?? 0} of ${s.daily_cap} transactions sent in the last 24 hours.`}
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="contained" disabled={busy} onClick={save}>Save</Button>
        {s?.has_key && (
          <Button size="small" color="error" disabled={busy} onClick={remove}>Remove key</Button>
        )}
      </Stack>
    </Paper>
  );
};
